import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import L from 'leaflet';
import api from '../lib/api';
import { useGeolocation } from '../hooks/use-geolocation';
import { haversineDistanceMeters } from '../utils/geo';
import MobileMap from '../components/maps/MobileMap';
import {
  ArrowLeft,
  MapPin,
  CheckCircle,
  Clock,
  Navigation,
  AlertCircle,
  Plus,
  X,
  Search,
  Loader2,
  FileWarning,
  List,
  Map as MapIcon,
  Sparkles,
  TrendingDown,
  ArrowRight,
} from 'lucide-react';
import StoreScoreBadge, { getCategoryColor } from '../components/route/StoreScoreBadge';
import RouteSummaryBar, { countUrgentStores } from '../components/route/RouteSummaryBar';

interface RouteStore {
  store_id: string;
  store_name: string;
  store_address: string;
  store_city: string;
  store_state: string;
  order: number;
  visited: boolean;
  visit_id?: string;
  check_in_time?: string;
  check_out_time?: string;
  perfect_store_score?: number;
  latitude?: number | null;
  longitude?: number | null;
  category?: string | null;
  score?: number | null;
}

interface TodayRoute {
  id: string;
  date: string;
  stores: RouteStore[];
  total_distance_km?: number | null;
  estimated_duration_min?: number | null;
}

interface NearbyStore {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  distance: number;
}

interface OptimizedStore {
  store_id: string;
  store_name: string;
  order: number;
  arrival_est: string;
  departure_est: string;
  distance_from_prev_km: number;
}

interface OptimizationResult {
  optimized_route: OptimizedStore[];
  total_distance_km: number;
  total_duration_min: number;
  original_distance_km: number;
  original_duration_min: number;
  savings_percent: number;
  algorithm: string;
  distance_source: string;
}

function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}

// ── Route Map View ──

function RouteMapView({
  stores,
  userLat,
  userLng,
  onStoreTap,
}: {
  stores: RouteStore[];
  userLat: number | null;
  userLng: number | null;
  onStoreTap: (storeId: string) => void;
}) {
  const handleMapReady = useCallback(
    (map: L.Map) => {
      const bounds = L.latLngBounds([]);

      // Add numbered markers with category-based colors
      stores.forEach((store) => {
        if (!store.latitude || !store.longitude) return;

        const color = store.visited
          ? '#22c55e'
          : store.check_in_time
          ? '#3b82f6'
          : getCategoryColor(store.category);
        const textColor = store.visited || store.check_in_time ? 'white' : 'white';

        const icon = L.divIcon({
          className: 'route-store-marker',
          html: `<div style="
            width: 32px; height: 32px; border-radius: 50%;
            background: ${color}; color: ${textColor};
            display: flex; align-items: center; justify-content: center;
            font-weight: 700; font-size: 14px;
            border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          ">${store.order}</div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });

        const marker = L.marker([store.latitude, store.longitude], { icon }).addTo(map);
        marker.bindTooltip(store.store_name, { direction: 'top', offset: [0, -18] });
        marker.on('click', () => onStoreTap(store.store_id));

        bounds.extend([store.latitude, store.longitude]);
      });

      // Draw polyline connecting stores in order
      const polylineCoords = stores
        .filter((s) => s.latitude && s.longitude)
        .sort((a, b) => a.order - b.order)
        .map((s) => [s.latitude!, s.longitude!] as [number, number]);

      if (polylineCoords.length >= 2) {
        L.polyline(polylineCoords, {
          color: '#6366f1',
          weight: 3,
          dashArray: '8, 6',
          opacity: 0.7,
        }).addTo(map);
      }

      // Include user position in bounds
      if (userLat && userLng) {
        bounds.extend([userLat, userLng]);
      }

      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40] });
      }
    },
    [stores, userLat, userLng, onStoreTap]
  );

  return (
    <MobileMap
      userLat={userLat}
      userLng={userLng}
      className="h-full w-full"
      onMapReady={handleMapReady}
    />
  );
}

// ── Main Route Component ──

export default function Route() {
  const [, setLocation] = useLocation();
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [showExtraVisit, setShowExtraVisit] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [gpsPosition, setGpsPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [showOptimize, setShowOptimize] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState<OptimizationResult | null>(null);
  const [localStoreOverrides, setLocalStoreOverrides] = useState<Record<string, number> | null>(null);
  const geo = useGeolocation();

  // Request GPS for map view and distance calculations
  useEffect(() => {
    geo.getCurrentPosition().then((coords) => {
      if (coords) {
        setGpsPosition({ lat: coords.latitude, lng: coords.longitude });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestGpsPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError('Geolocalizacao nao suportada pelo navegador.');
      return;
    }
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsPosition({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (err) => {
        setGpsError(
          err.code === 1
            ? 'Permissao de localizacao negada.'
            : 'Nao foi possivel obter a localizacao.'
        );
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const optimizeMutation = useMutation({
    mutationFn: (params: { route_id: string; current_lat: number; current_lng: number; visited_store_ids: string[] }) =>
      api.post('/routes/optimize/live', params).then((res) => res.data.data as OptimizationResult),
    onSuccess: (data) => {
      setOptimizationResult(data);
    },
  });

  const handleOpenOptimize = useCallback(() => {
    setShowOptimize(true);
    setOptimizationResult(null);
    optimizeMutation.reset();
  }, [optimizeMutation]);

  const handleApplyOptimization = useCallback(() => {
    if (!optimizationResult) return;
    const overrides: Record<string, number> = {};
    optimizationResult.optimized_route.forEach((item) => {
      overrides[item.store_id] = item.order;
    });
    setLocalStoreOverrides(overrides);
    setShowOptimize(false);
    setOptimizationResult(null);
  }, [optimizationResult]);

  // Trigger optimization API call when bottom sheet opens
  useEffect(() => {
    if (showOptimize && gpsPosition && route) {
      const visitedIds = (route.stores || []).filter((s) => s.visited).map((s) => s.store_id);
      optimizeMutation.mutate({
        route_id: route.id,
        current_lat: gpsPosition.lat,
        current_lng: gpsPosition.lng,
        visited_store_ids: visitedIds,
      });
    }
  }, [showOptimize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showExtraVisit && !gpsPosition && !gpsError) {
      requestGpsPosition();
    }
  }, [showExtraVisit, gpsPosition, gpsError, requestGpsPosition]);

  const {
    data: nearbyStores,
    isLoading: isLoadingNearby,
    error: nearbyError,
  } = useQuery<NearbyStore[]>({
    queryKey: ['stores', 'nearby', gpsPosition?.lat, gpsPosition?.lng],
    queryFn: () =>
      api
        .get('/stores/nearby', {
          params: { lat: gpsPosition!.lat, lng: gpsPosition!.lng, radius: 5 },
        })
        .then((res) => res.data.data),
    enabled: showExtraVisit && !!gpsPosition,
  });

  const filteredNearbyStores = (nearbyStores || []).filter((store) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      store.name.toLowerCase().includes(term) ||
      store.address.toLowerCase().includes(term)
    );
  });

  const { data: route, isLoading, error } = useQuery<TodayRoute>({
    queryKey: ['route', 'today'],
    queryFn: () => api.get('/routes/today').then(res => res.data.data),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando roteiro...</p>
        </div>
      </div>
    );
  }

  if (error || !route) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <header className="bg-primary text-white p-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setLocation('/')}
              className="p-2 -ml-2 rounded-full hover:bg-white/20"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-bold">Meu Roteiro</h1>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Nenhum roteiro disponivel
            </h2>
            <p className="text-gray-600 mb-6">
              Voce nao tem lojas programadas para hoje
            </p>
            <button
              onClick={() => setLocation('/')}
              className="px-6 py-3 bg-primary text-white rounded-lg font-semibold"
            >
              Voltar ao inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  const stores = (route.stores || []).map((s) => {
    if (localStoreOverrides && localStoreOverrides[s.store_id] !== undefined) {
      return { ...s, order: localStoreOverrides[s.store_id] };
    }
    return s;
  });
  const visitedCount = stores.filter((s) => s.visited).length;
  const unvisitedCount = stores.filter((s) => !s.visited).length;
  const totalStores = stores.length;
  const progressPercentage =
    totalStores > 0 ? Math.round((visitedCount / totalStores) * 100) : 0;

  const canOptimize = totalStores > 0 && !!gpsPosition && unvisitedCount >= 2;

  const sortedStores = [...stores].sort((a, b) => a.order - b.order);
  const nextStore = sortedStores.find((s) => !s.visited);

  // Distance to next store
  const distanceToNext =
    nextStore?.latitude && nextStore?.longitude && gpsPosition
      ? haversineDistanceMeters(gpsPosition.lat, gpsPosition.lng, nextStore.latitude, nextStore.longitude)
      : null;

  const openInMaps = (store: RouteStore) => {
    const address = encodeURIComponent(
      `${store.store_address}, ${store.store_city} - ${store.store_state}`
    );
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${address}`;
    window.open(mapsUrl, '_blank');
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4 pb-8">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => setLocation('/')}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Meu Roteiro</h1>
            <p className="text-sm opacity-80">
              {new Date(route.date).toLocaleDateString('pt-BR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </p>
          </div>
          {/* Map/List toggle */}
          <div className="flex bg-white/20 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-white/30' : ''}`}
              aria-label="Lista"
            >
              <List className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`p-2 ${viewMode === 'map' ? 'bg-white/30' : ''}`}
              aria-label="Mapa"
            >
              <MapIcon className="h-5 w-5" />
            </button>
          </div>
          {/* Re-optimize button */}
          {canOptimize && (
            <button
              onClick={handleOpenOptimize}
              className="p-2 rounded-lg bg-white/20 active:bg-white/30"
              aria-label="Re-otimizar rota"
            >
              <Sparkles className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Progress Card */}
        <div className="bg-white rounded-xl p-4 text-gray-900 shadow-lg -mb-12">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600">Progresso do Dia</span>
            <span className="text-lg font-bold text-primary">
              {visitedCount}/{totalStores}
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {progressPercentage}% completo
          </p>
        </div>
      </header>

      {/* Content */}
      {viewMode === 'map' ? (
        <div className="flex-1 pt-14 relative">
          <RouteMapView
            stores={sortedStores}
            userLat={gpsPosition?.lat ?? null}
            userLng={gpsPosition?.lng ?? null}
            onStoreTap={(storeId) => setLocation(`/visit/${storeId}`)}
          />
        </div>
      ) : (
        <main className="flex-1 p-4 pt-16 space-y-4">
          {/* Route Summary Bar */}
          <RouteSummaryBar
            storeCount={totalStores}
            totalDistanceKm={route.total_distance_km}
            estimatedDurationMin={route.estimated_duration_min}
            urgentCount={countUrgentStores(stores)}
          />

          {/* Next Store Card */}
          {nextStore && (
            <div className="bg-gradient-to-br from-primary to-primary/80 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center gap-2 mb-3">
                <Navigation className="h-5 w-5" />
                <span className="text-sm font-medium">Proxima Visita</span>
                {distanceToNext !== null && (
                  <span className="ml-auto text-xs bg-white/20 rounded-full px-2 py-0.5">
                    {formatDistance(distanceToNext)}
                  </span>
                )}
              </div>
              <h3 className="text-lg font-bold mb-1">{nextStore.store_name}</h3>
              <p className="text-sm opacity-90 mb-4">
                {nextStore.store_address}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setLocation(`/visit/${nextStore.store_id}`)}
                  className="flex-1 py-2 bg-white text-primary rounded-lg font-semibold text-sm"
                >
                  Iniciar Visita
                </button>
                <button
                  onClick={() => openInMaps(nextStore)}
                  className="px-4 py-2 bg-white/20 rounded-lg"
                >
                  <Navigation className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* All Stores List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">
                Todas as Lojas ({totalStores})
              </h2>
            </div>

            <div className="divide-y divide-gray-100">
              {sortedStores.map((store) => (
                <div
                  key={store.store_id}
                  className={`p-4 ${store.visited ? 'bg-green-50/50' : ''}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Order Number */}
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                        store.visited
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {store.order}
                    </div>

                    {/* Store Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h3 className="font-medium text-gray-900 truncate">
                              {store.store_name}
                            </h3>
                            <StoreScoreBadge category={store.category} compact />
                          </div>
                          <p className="text-sm text-gray-600 truncate">
                            {store.store_address}
                          </p>
                          <p className="text-xs text-gray-500">
                            {store.store_city} - {store.store_state}
                          </p>
                        </div>
                      </div>

                      {/* Status Badge */}
                      {store.visited ? (
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center gap-2 text-xs text-green-700">
                            <CheckCircle className="h-4 w-4" />
                            <span className="font-medium">Visitada</span>
                            {store.check_out_time && (
                              <span className="text-green-600">
                                {new Date(store.check_out_time).toLocaleTimeString(
                                  'pt-BR',
                                  { hour: '2-digit', minute: '2-digit' }
                                )}
                              </span>
                            )}
                          </div>
                          {store.perfect_store_score !== undefined && (
                            <div className="text-xs text-green-700">
                              Score: {store.perfect_store_score}%
                            </div>
                          )}
                        </div>
                      ) : store.check_in_time ? (
                        <div className="mt-2 flex items-center gap-2 text-xs text-blue-700">
                          <Clock className="h-4 w-4" />
                          <span className="font-medium">Em andamento</span>
                          <span className="text-blue-600">
                            desde{' '}
                            {new Date(store.check_in_time).toLocaleTimeString(
                              'pt-BR',
                              { hour: '2-digit', minute: '2-digit' }
                            )}
                          </span>
                        </div>
                      ) : (
                        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                          <MapPin className="h-4 w-4" />
                          <span>Pendente</span>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => setLocation(`/visit/${store.store_id}`)}
                          className="flex-1 py-2 px-4 bg-primary text-white rounded-lg text-sm font-medium active:bg-primary/90"
                        >
                          {store.visited
                            ? 'Ver Detalhes'
                            : store.check_in_time
                            ? 'Continuar'
                            : 'Iniciar'}
                        </button>
                        {!store.visited && !store.check_in_time && (
                          <button
                            onClick={() => setLocation(`/justify/${store.store_id}`)}
                            className="px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm font-medium active:bg-amber-100 flex items-center gap-1"
                          >
                            <FileWarning className="h-4 w-4" />
                            <span>Justificar</span>
                          </button>
                        )}
                        <button
                          onClick={() => openInMaps(store)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg active:bg-gray-200"
                        >
                          <Navigation className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Completion Message */}
          {visitedCount === totalStores && totalStores > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-green-900 mb-1">
                Parabens!
              </h3>
              <p className="text-sm text-green-700">
                Voce completou todas as visitas do dia!
              </p>
            </div>
          )}
        </main>
      )}

      {/* FAB - Visita Extra */}
      <button
        onClick={() => setShowExtraVisit(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center active:bg-primary/90 z-40 hover:shadow-xl transition-shadow"
        aria-label="Visita Extra"
      >
        <Plus className="h-6 w-6" />
      </button>

      {/* Extra Visit Modal */}
      {showExtraVisit && (
        <div className="fixed inset-0 z-50 flex flex-col">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowExtraVisit(false)}
          />

          {/* Modal Sheet */}
          <div className="relative mt-auto bg-white rounded-t-2xl max-h-[85vh] flex flex-col animate-slide-up">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Visita Extra</h2>
              <button
                onClick={() => setShowExtraVisit(false)}
                className="p-2 -mr-2 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar loja por nome ou endereco..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {gpsError && (
                <div className="p-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">{gpsError}</p>
                      <button
                        onClick={requestGpsPosition}
                        className="mt-1 text-red-800 underline text-xs"
                      >
                        Tentar novamente
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!gpsPosition && !gpsError && (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <Loader2 className="h-8 w-8 text-primary animate-spin mb-3" />
                  <p className="text-sm text-gray-600">Obtendo sua localizacao...</p>
                </div>
              )}

              {gpsPosition && isLoadingNearby && (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <Loader2 className="h-8 w-8 text-primary animate-spin mb-3" />
                  <p className="text-sm text-gray-600">Buscando lojas proximas...</p>
                </div>
              )}

              {gpsPosition && nearbyError && (
                <div className="p-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <span>Erro ao buscar lojas proximas.</span>
                  </div>
                </div>
              )}

              {gpsPosition && !isLoadingNearby && !nearbyError && filteredNearbyStores.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <MapPin className="h-10 w-10 text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500 text-center">
                    {searchTerm.trim()
                      ? 'Nenhuma loja encontrada para esta busca.'
                      : 'Nenhuma loja encontrada nas proximidades.'}
                  </p>
                </div>
              )}

              {filteredNearbyStores.length > 0 && (
                <div className="divide-y divide-gray-100">
                  {filteredNearbyStores.map((store) => (
                    <div key={store.id} className="p-4 flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                        <MapPin className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">
                          {store.name}
                        </h3>
                        <p className="text-sm text-gray-600 truncate">
                          {store.address}
                        </p>
                        <p className="text-xs text-gray-500">
                          {store.city} - {store.state}
                        </p>
                        <span className="inline-block mt-1 text-xs text-primary font-medium">
                          {store.distance < 1
                            ? `${Math.round(store.distance * 1000)}m`
                            : `${store.distance.toFixed(1)}km`}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setShowExtraVisit(false);
                          setLocation(`/visit/${store.id}`);
                        }}
                        className="flex-shrink-0 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium active:bg-primary/90"
                      >
                        Iniciar Visita
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Optimization Bottom Sheet */}
      {showOptimize && (
        <div className="fixed inset-0 z-50 flex flex-col">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 z-40"
            onClick={() => setShowOptimize(false)}
          />

          {/* Bottom Sheet */}
          <div className="relative mt-auto bg-white rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col z-50 animate-slide-up">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-bold text-gray-900">Re-otimizar Rota</h2>
              </div>
              <button
                onClick={() => setShowOptimize(false)}
                className="p-2 -mr-2 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Loading State */}
              {optimizeMutation.isPending && (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                  <Loader2 className="h-10 w-10 text-primary animate-spin mb-4" />
                  <p className="text-sm font-medium text-gray-700">Calculando melhor rota...</p>
                  <p className="text-xs text-gray-500 mt-1">Isso pode levar alguns segundos</p>
                </div>
              )}

              {/* Error State */}
              {optimizeMutation.isError && (
                <div className="p-4">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                    <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
                    <p className="text-sm font-medium text-red-800 mb-1">Erro ao otimizar rota</p>
                    <p className="text-xs text-red-600 mb-4">
                      {(optimizeMutation.error as Error)?.message || 'Tente novamente'}
                    </p>
                    <button
                      onClick={() => {
                        if (gpsPosition && route) {
                          const visitedIds = (route.stores || []).filter((s) => s.visited).map((s) => s.store_id);
                          optimizeMutation.mutate({
                            route_id: route.id,
                            current_lat: gpsPosition.lat,
                            current_lng: gpsPosition.lng,
                            visited_store_ids: visitedIds,
                          });
                        }
                      }}
                      className="px-6 py-2 bg-red-600 text-white rounded-lg text-sm font-medium active:bg-red-700"
                    >
                      Tentar novamente
                    </button>
                  </div>
                </div>
              )}

              {/* Success State */}
              {optimizationResult && (
                <div className="p-4 space-y-4">
                  {/* Savings Badge */}
                  <div className="flex items-center justify-center">
                    <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 rounded-full px-4 py-2">
                      <TrendingDown className="h-4 w-4" />
                      <span className="text-sm font-semibold">
                        Economia: {(optimizationResult.original_distance_km - optimizationResult.total_distance_km).toFixed(1)} km ({optimizationResult.savings_percent.toFixed(0)}%)
                      </span>
                    </div>
                  </div>

                  {/* Distance & Duration Comparison */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Rota otimizada</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Distancia</p>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-400 line-through">
                            {optimizationResult.original_distance_km.toFixed(1)} km
                          </span>
                          <ArrowRight className="h-3 w-3 text-gray-400" />
                          <span className="text-sm font-bold text-green-700">
                            {optimizationResult.total_distance_km.toFixed(1)} km
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Duracao</p>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-400 line-through">
                            {optimizationResult.original_duration_min} min
                          </span>
                          <ArrowRight className="h-3 w-3 text-gray-400" />
                          <span className="text-sm font-bold text-green-700">
                            {optimizationResult.total_duration_min} min
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Optimized Store Order */}
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-gray-900">Nova ordem de visitas</h3>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {optimizationResult.optimized_route.map((item) => (
                        <div key={item.store_id} className="px-4 py-3 flex items-center gap-3">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                            {item.order}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{item.store_name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {item.arrival_est && (
                                <span className="text-xs text-gray-500">
                                  Chegada: {item.arrival_est}
                                </span>
                              )}
                              {item.distance_from_prev_km > 0 && (
                                <span className="text-xs text-gray-400">
                                  ({item.distance_from_prev_km.toFixed(1)} km)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pb-2">
                    <button
                      onClick={() => setShowOptimize(false)}
                      className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-xl text-sm font-semibold active:bg-gray-50"
                    >
                      Manter atual
                    </button>
                    <button
                      onClick={handleApplyOptimization}
                      className="flex-1 py-3 px-4 bg-primary text-white rounded-xl text-sm font-semibold active:bg-primary/90"
                    >
                      Usar nova rota
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
